/**
 * cl-core/mode — 현장 문서 모드 판정 (지시서 §1-2). 순수 TS.
 *
 * 모드 판정은 이 `getSiteDocMode` 하나로만 한다.
 *  - 표준: 핵심 팩트 8행(위치·총세대·일반분양·규모·시공·시행·분양 시기·입주 예정) 중 4행 이상,
 *          또는 2행 이상이면서 일정·가격·AI 이미지 중 하나가 있을 때
 *  - 채움: 나머지 전부
 * 「있다」의 정의: 금지 문자열 검사(cleanCell)를 통과한 값 = 화면 표에 실제로 서는 행.
 */
import { cleanCell } from './forbidden';

export type SiteDocMode = 'standard' | 'fill';

export const CORE_FACT_KEYS = ['location', 'totalUnits', 'generalUnits', 'scale', 'builder', 'developer', 'salePeriod', 'moveIn'] as const;
export type CoreFactKey = (typeof CORE_FACT_KEYS)[number];

export const CORE_FACT_LABEL: Record<CoreFactKey, string> = {
  location: '위치',
  totalUnits: '총세대',
  generalUnits: '일반분양',
  scale: '규모',
  builder: '시공',
  developer: '시행',
  salePeriod: '분양 시기',
  moveIn: '입주 예정',
};

export type CoreFacts = Partial<Record<CoreFactKey, unknown>>;

export interface SiteDocModeInput {
  facts: CoreFacts;
  /** 확정 일정 행이 1개 이상 */
  hasSchedule?: boolean;
  /** 공고 원문 등 실가격(합성가 제외) */
  hasPrice?: boolean;
  /** AI 상상도 1장 이상 */
  hasAiImage?: boolean;
}

/** 값이 «서는» 핵심 팩트 행 수. */
export function countCoreFacts(facts: CoreFacts): number {
  let n = 0;
  for (const k of CORE_FACT_KEYS) {
    const v = facts[k];
    if (typeof v === 'number') {
      if (Number.isFinite(v) && v > 0) n++;
    } else if (cleanCell(v) != null) n++;
  }
  return n;
}

export function getSiteDocMode(input: SiteDocModeInput): SiteDocMode {
  const n = countCoreFacts(input.facts || {});
  if (n >= 4) return 'standard';
  if (n >= 2 && (input.hasSchedule || input.hasPrice || input.hasAiImage)) return 'standard';
  return 'fill';
}

/** FAQ 문항 수(§1-2 #9: 표준 4 · 채움 3). */
export function faqCountFor(mode: SiteDocMode): number {
  return mode === 'standard' ? 4 : 3;
}

/**
 * 현장 문서 섹션 순서(§1-2 · 게이트 CL1). `data-cl-section` 값이 이 순서로 나온다(없는 섹션은 건너뜀).
 * lead_form_ad 는 광고 유입 변형(클라이언트)에서만 hero 바로 뒤에 선다 — 서버 HTML 에는 없다.
 */
export const SITE_DOC_SECTIONS: Record<SiteDocMode, readonly string[]> = {
  standard: ['breadcrumb', 'hero', 'lead', 'facts', 'schedule', 'lead_form', 'location', 'price', 'gallery', 'faq', 'related', 'actions', 'ad'],
  fill: ['breadcrumb', 'hero', 'lead', 'facts', 'schedule', 'lead_form', 'location', 'price', 'gallery', 'faq', 'related', 'actions', 'ad'],
};
